import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Tooltip,
    TablePagination,
    CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PageHeader1 from '../../../components/common/PageHeader1';
import { handleUnauthorized } from '../../../TokenAuth/auth';
import { formatUTCDate } from '../../../utils/dateUtils';

const BASE_URL = process.env.REACT_APP_BASEURL;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function ValidateAbusiveWords() {
    const token = localStorage.getItem('astroVani-admin-token');

    // 1. Toggle State
    const [isFilterEnabled, setIsFilterEnabled] = useState(false);
    const [statusLoading, setStatusLoading] = useState(true);
    const [toggleUpdating, setToggleUpdating] = useState(false);

    // 2. File Upload State
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileStats, setFileStats] = useState(null);
    const [previewWords, setPreviewWords] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // 3. Existing Filter Words State (GET /admin/chat-filter/words)
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPage = Number.parseInt(searchParams.get('page') || '0', 10);
    const initialPageSize = Number.parseInt(searchParams.get('pageSize') || '20', 10);

    const [wordsList, setWordsList] = useState([]);
    const [wordsLoading, setWordsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('chips'); // default 'chips'
    const [paginationModel, setPaginationModel] = useState({
        page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
        pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 20,
    });
    const [paginationDetail, setPaginationDetail] = useState({
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit: 20,
    });
    const [copiedWord, setCopiedWord] = useState(null);

    // 4. Delete Word State (MUI Dialog matching AstrologerList)
    const [wordToDelete, setWordToDelete] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // 5. Show / Hide Word Visibility State (Default: all words hidden)
    const [showAllWords, setShowAllWords] = useState(false);
    const [toggledWordIds, setToggledWordIds] = useState(new Set());

    // Check if word is currently hidden (default is hidden/masked)
    const isWordHidden = (id) => {
        if (showAllWords) {
            // When 'Show All' is active: word is hidden only if individually toggled off
            return toggledWordIds.has(id);
        }
        // DEFAULT: all words hidden unless individually toggled on
        return !toggledWordIds.has(id);
    };

    // Toggle individual word visibility
    const toggleWordVisibility = (id) => {
        setToggledWordIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // Toggle all words visibility
    const toggleAllWords = () => {
        setShowAllWords((prev) => !prev);
        setToggledWordIds(new Set());
    };

    // ==========================================
    // 1. Fetch Current Abusive Filter Status (GET)
    // ==========================================
    const fetchFilterStatus = async () => {
        try {
            setStatusLoading(true);
            const response = await axios.get(`${BASE_URL}/admin/abusiveFilterEnabled`, {
                headers: {
                    Authorization: token,
                },
            });

            const resData = response?.data;
            const val =
                resData?.data?.value ??
                resData?.value ??
                resData?.data?.isEnabled ??
                resData?.data;

            if (typeof val === 'boolean') {
                setIsFilterEnabled(val);
            } else if (typeof resData?.data?.abusiveFilterEnabled === 'boolean') {
                setIsFilterEnabled(resData.data.abusiveFilterEnabled);
            }
        } catch (error) {
            console.error('Failed to fetch abusive filter status:', error);
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }
            toast.error(
                error?.response?.data?.message || 'Failed to load abusive filter status'
            );
        } finally {
            setStatusLoading(false);
        }
    };

    // ==========================================
    // 2. Fetch Filter Words List (GET /admin/chat-filter/words)
    // ==========================================
    const fetchFilteredWords = useCallback(
        async (targetPage = paginationModel.page + 1, targetLimit = paginationModel.pageSize) => {
            try {
                setWordsLoading(true);
                const params = {
                    page: targetPage,
                    limit: targetLimit,
                };

                let response;
                try {
                    response = await axios.get(`${BASE_URL}/admin/chat-filter/words`, {
                        headers: {
                            Authorization: token,
                        },
                        params,
                    });
                } catch (err) {
                    if (err?.response?.status === 404) {
                        response = await axios.get(`${BASE_URL}/admin//chat-filter/words`, {
                            headers: {
                                Authorization: token,
                            },
                            params,
                        });
                    } else {
                        throw err;
                    }
                }

                const resData = response?.data;
                const rawList = resData?.data || [];

                // Extract abusiveWord according to API response:
                // { _id, abusiveWord, isActive, createdAt, version }
                const normalized = rawList.map((item, idx) => {
                    if (typeof item === 'string') {
                        return {
                            id: (targetPage - 1) * targetLimit + idx + 1,
                            word: item,
                            isActive: true,
                            createdAt: null,
                            version: null,
                        };
                    }
                    return {
                        id: item._id || (targetPage - 1) * targetLimit + idx + 1,
                        _id: item._id,
                        word: item.abusiveWord || item.word || item.name || '',
                        isActive: item.isActive !== undefined ? item.isActive : true,
                        createdAt: item.createdAt || null,
                        version: item.version ?? null,
                    };
                });

                setWordsList(normalized);
                setToggledWordIds(new Set());

                if (resData?.paginationDetail) {
                    setPaginationDetail({
                        totalDocs: resData.paginationDetail.totalDocs ?? normalized.length,
                        totalPages: resData.paginationDetail.totalPages ?? 1,
                        page: resData.paginationDetail.page ?? targetPage,
                        limit: resData.paginationDetail.limit ?? targetLimit,
                    });
                } else {
                    setPaginationDetail({
                        totalDocs: normalized.length,
                        totalPages: 1,
                        page: targetPage,
                        limit: targetLimit,
                    });
                }
            } catch (error) {
                console.error('Failed to fetch abusive filter words:', error);
                if (error?.response?.status === 401) {
                    handleUnauthorized();
                    return;
                }
                toast.error(
                    error?.response?.data?.message || 'Failed to load filter words list'
                );
            } finally {
                setWordsLoading(false);
            }
        },
        [token, paginationModel.page, paginationModel.pageSize]
    );

    // Initial fetch of status
    useEffect(() => {
        fetchFilterStatus();
    }, []);

    // Fetch words whenever page or pageSize changes
    useEffect(() => {
        fetchFilteredWords(paginationModel.page + 1, paginationModel.pageSize);
    }, [paginationModel.page, paginationModel.pageSize]);

    // Sync to URL search params (e.g. ?page=0&pageSize=10)
    const didInitUrlSyncRef = useRef(false);
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }
        const params = new URLSearchParams(searchParams);
        params.set('page', String(paginationModel.page));
        params.set('pageSize', String(paginationModel.pageSize));
        setSearchParams(params, { replace: true });
    }, [paginationModel.page, paginationModel.pageSize, setSearchParams]);

    // Handle pagination page change (0-indexed for MUI TablePagination)
    const handleChangePage = (event, newPage) => {
        setPaginationModel((prev) => ({ ...prev, page: newPage }));
    };

    // Handle rowsPerPage change
    const handleChangeRowsPerPage = (event) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPaginationModel({ page: 0, pageSize: newPageSize });
    };

    // ==========================================
    // 3. Toggle Abusive Filter Status (PATCH)
    // ==========================================
    const handleToggleFilter = async (e) => {
        const nextValue = e.target.checked;
        const previousValue = isFilterEnabled;

        setIsFilterEnabled(nextValue);
        setToggleUpdating(true);

        try {
            const response = await axios.patch(
                `${BASE_URL}/admin/abusiveFilterEnabled`,
                { value: nextValue },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token,
                    },
                }
            );

            const msg =
                response?.data?.message ||
                `Abusive word filter ${nextValue ? 'enabled' : 'disabled'} successfully!`;
            toast.success(msg);
        } catch (error) {
            console.error('Failed to toggle abusive filter:', error);
            setIsFilterEnabled(previousValue);
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }
            toast.error(
                error?.response?.data?.message || 'Failed to update abusive filter status'
            );
        } finally {
            setToggleUpdating(false);
        }
    };

    // ==========================================
    // 4. File Selection & Validations
    // ==========================================
    const processFile = (file) => {
        if (!file) return;

        // 1. Extension Validation
        const validExtensions = ['.xlsx', '.xls'];
        const fileName = file.name.toLowerCase();
        const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

        if (!isValid) {
            toast.error('Invalid file format. Only Excel files (.xlsx, .xls) are allowed.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // 2. File Size Validation (Max 10 MB)
        if (file.size > MAX_FILE_SIZE_BYTES) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
            toast.error(
                `File size (${sizeMB} MB) exceeds maximum allowed limit of ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`
            );
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // 3. Empty File Validation
        if (file.size === 0) {
            toast.error('The selected file is empty (0 bytes). Please choose a valid Excel file.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setSelectedFile(file);

        // Client-side inspection using XLSX
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                let detectedKey = '';
                const allWords = [];

                // Check if row 0 (A1) is a key/category header (e.g. 'abusing_words' or 'key')
                if (jsonData.length > 0 && Array.isArray(jsonData[0])) {
                    const firstCell = String(jsonData[0][0] || '').trim();
                    const hasCommaInRow1 =
                        jsonData.length > 1 &&
                        Array.isArray(jsonData[1]) &&
                        String(jsonData[1][0] || '').includes(',');

                    if (
                        firstCell &&
                        (firstCell.toLowerCase() === 'abusing_words' ||
                            firstCell.toLowerCase() === 'key' ||
                            (!firstCell.includes(',') && hasCommaInRow1))
                    ) {
                        detectedKey = firstCell;
                    }
                }

                jsonData.forEach((row, rowIdx) => {
                    if (Array.isArray(row)) {
                        row.forEach((cell, cellIdx) => {
                            if (cell !== undefined && cell !== null) {
                                const str = String(cell).trim();
                                if (str.length > 0) {
                                    // If cell A1 is the key header, skip adding it as an abusive word
                                    if (rowIdx === 0 && cellIdx === 0 && detectedKey && str === detectedKey) {
                                        return;
                                    }

                                    // Split comma-separated words (e.g. in cell A2: sassa,sa,add,as,ad...)
                                    if (str.includes(',')) {
                                        const splitWords = str
                                            .split(',')
                                            .map((w) => w.trim())
                                            .filter(Boolean);
                                        allWords.push(...splitWords);
                                    } else {
                                        allWords.push(str);
                                    }
                                }
                            }
                        });
                    }
                });

                if (allWords.length === 0) {
                    toast.warning('No text words found in the uploaded file.');
                }

                setFileStats({
                    fileName: file.name,
                    fileSize:
                        file.size < 1024 * 1024
                            ? (file.size / 1024).toFixed(1) + ' KB'
                            : (file.size / (1024 * 1024)).toFixed(2) + ' MB',
                    sheetCount: workbook.SheetNames.length,
                    activeSheet: firstSheetName,
                    keyName: detectedKey || null,
                    totalWords: allWords.length,
                });

                setPreviewWords(allWords.slice(0, 30));
            } catch (err) {
                console.warn('Could not inspect excel preview:', err);
                setFileStats({
                    fileName: file.name,
                    fileSize:
                        file.size < 1024 * 1024
                            ? (file.size / 1024).toFixed(1) + ' KB'
                            : (file.size / (1024 * 1024)).toFixed(2) + ' MB',
                    sheetCount: 1,
                    activeSheet: 'Sheet 1',
                    keyName: null,
                    totalWords: 'N/A',
                });
                setPreviewWords([]);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        setFileStats(null);
        setPreviewWords([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // ==========================================
    // 4.1 Download Sample Excel Template
    // ==========================================
    const handleDownloadSampleFile = (e) => {
        if (e) {
            e.stopPropagation();
        }
        try {
            // Format: A1 = 'abusing_words', A2 = comma-separated words
            const sampleData = [
                ['abusing_words'],
                ['badword1, badword2, abusiveword, offensive_term, sample_abuse_word'],
            ];

            const ws = XLSX.utils.aoa_to_sheet(sampleData);

            // Set column width so content is readable
            ws['!cols'] = [{ wch: 80 }];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sample_Abusive_Words');

            XLSX.writeFile(wb, 'sample_abusive_words.xlsx');
            toast.success('Sample Excel file downloaded successfully');
        } catch (error) {
            console.error('Error generating sample file:', error);
            toast.error('Failed to download sample file');
        }
    };

    // ==========================================
    // 5. Upload Filter Words (PUT /admin/chat-filter/settings)
    // ==========================================
    const handleUploadSubmit = async () => {
        if (!selectedFile) {
            toast.warning('Please select an Excel file to upload');
            return;
        }

        // Validate size before upload
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            toast.error(`File size exceeds maximum allowed limit of ${MAX_FILE_SIZE_MB} MB.`);
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('key', selectedFile);

            const response = await axios.put(
                `${BASE_URL}/admin/chat-filter/settings`,
                formData,
                {
                    headers: {
                        Authorization: token,
                    },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const percent = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            setUploadProgress(percent);
                        }
                    },
                }
            );

            const msg =
                response?.data?.message ||
                'Filter words uploaded successfully!';
            toast.success(msg);
            handleClearFile();

            // Refresh the active words list from backend (back to page 1)
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            fetchFilteredWords(1, paginationModel.pageSize);
        } catch (error) {
            console.error('Failed to upload filter words file:', error);
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }
            toast.error(
                error?.response?.data?.message ||
                'Failed to upload filter words. Please try again.'
            );
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    // Copy to clipboard
    const handleCopyWord = (word) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(word);
            setCopiedWord(word);
            setTimeout(() => setCopiedWord(null), 1500);
        }
    };

    // ==========================================
    // 6. Delete Word (DELETE /admin/chat-filter/words/:id)
    // ==========================================
    const handleDeleteClick = (item) => {
        setWordToDelete(item);
        setOpenDeleteDialog(true);
    };

    const handleCloseDelete = () => {
        if (!isDeleting) {
            setOpenDeleteDialog(false);
            setWordToDelete(null);
        }
    };

    const confirmDeleteWord = async () => {
        if (!wordToDelete) return;
        const targetId = wordToDelete._id || wordToDelete.id;
        if (!targetId) {
            toast.error('Word ID not found for deletion');
            return;
        }

        try {
            setIsDeleting(true);
            let response;
            try {
                response = await axios.delete(
                    `${BASE_URL}/admin/chat-filter/words/${targetId}`,
                    {
                        headers: {
                            Authorization: token,
                        },
                    }
                );
            } catch (err) {
                if (err?.response?.status === 404) {
                    response = await axios.delete(
                        `${BASE_URL}/admin//chat-filter/words/${targetId}`,
                        {
                            headers: {
                                Authorization: token,
                            },
                        }
                    );
                } else {
                    throw err;
                }
            }

            if (response?.data?.success !== false) {
                toast.success(
                    response?.data?.message || `Word "${wordToDelete.word}" deleted successfully`
                );
                handleCloseDelete();

                // If this was the only word on the current page and page > 0, go to previous page
                const isLastItemOnPage = wordsList.length === 1 && paginationModel.page > 0;
                if (isLastItemOnPage) {
                    setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }));
                } else {
                    fetchFilteredWords(paginationModel.page + 1, paginationModel.pageSize);
                }
            } else {
                toast.error(response?.data?.message || 'Failed to delete word');
            }
        } catch (error) {
            console.error('Failed to delete abusive word:', error);
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }
            toast.error(
                error?.response?.data?.message || 'Failed to delete abusive word'
            );
        } finally {
            setIsDeleting(false);
        }
    };

    // DataGrid columns definition matching AstrologerList UI
    const columns = [
        {
            field: 'sono',
            headerName: 'S.No',
            width: 80,
            filterable: false,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => (
                <span style={{ color: '#64748b', fontWeight: 600 }}>{row.sono}</span>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => {
                const isHidden = isWordHidden(row.id);
                const isCopied = copiedWord === row.word;
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        <Tooltip title={isHidden ? 'Show Word' : 'Hide Word'}>
                            <IconButton
                                size="small"
                                onClick={() => toggleWordVisibility(row.id)}
                                sx={{ color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}
                            >
                                <i className={`icofont-${isHidden ? 'eye' : 'eye-blocked'}`} style={{ fontSize: '18px' }}></i>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isCopied ? 'Copied!' : 'Copy Word'}>
                            <IconButton
                                size="small"
                                onClick={() => handleCopyWord(row.word)}
                                sx={{ color: isCopied ? '#4caf50' : '#64748b', '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' } }}
                            >
                                <i className={`icofont-${isCopied ? 'check' : 'copy'}`} style={{ fontSize: '18px' }}></i>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Word">
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(row.rawItem || row)}
                                sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}
                            >
                                <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'word',
            headerName: 'Abusive Word',
            flex: 1,
            minWidth: 260,
            renderCell: ({ row }) => {
                const isHidden = isWordHidden(row.id);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {isHidden ? (
                            <span style={{ letterSpacing: '2px', fontWeight: 'bold', color: '#94a3b8' }}>
                                ••••••••
                            </span>
                        ) : (
                            <span style={{ fontWeight: 600, color: '#dc2626', fontSize: '14px' }}>
                                {row.word}
                            </span>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'createdAt',
            headerName: 'Created Date',
            width: 240,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '13px', color: '#475569' }}>
                    {row.createdAt ? (
                        <span>
                            {formatUTCDate(row.createdAt, { format: 'date', separator: '/' })}{' '}
                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>
                                ({formatUTCDate(row.createdAt, { format: 'time' })})
                            </small>
                        </span>
                    ) : (
                        '-'
                    )}
                </Box>
            ),
        },
    ];

    const rows = wordsList.map((item, idx) => ({
        id: item._id || item.id || `word-${idx}`,
        sono: paginationModel.page * paginationModel.pageSize + idx + 1,
        word: item.word,
        createdAt: item.createdAt,
        rawItem: item,
    }));

    return (
        <div className="container-xxl py-3">
            {/* Page Title & Breadcrumb */}
            <PageHeader1 pagetitle="Validate Abusive Words" />

            <div className="row g-3">
                {/* ================================================= */}
                {/* 1. MASTER FILTER TOGGLE HERO CARD (Single Line)   */}
                {/* ================================================= */}
                <div className="col-12">
                    <div
                        className="card border-0 shadow-sm"
                        style={{
                            borderRadius: '14px',
                            background: isFilterEnabled
                                ? 'linear-gradient(135deg, #f0fdf4 0%, #e6f7ec 100%)'
                                : 'linear-gradient(135deg, #fff7ed 0%, #fef3e7 100%)',
                            border: isFilterEnabled ? '1px solid #bbf7d0' : '1px solid #fed7aa',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                {/* Left: Icon + Title + Description */}
                                <div className="d-flex align-items-center gap-3" style={{ flex: '1 1 500px' }}>
                                    <div
                                        style={{
                                            width: '54px',
                                            height: '54px',
                                            borderRadius: '12px',
                                            background: isFilterEnabled ? '#16a34a' : '#ea580c',
                                            color: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '26px',
                                            flexShrink: 0,
                                            boxShadow: isFilterEnabled
                                                ? '0 6px 16px rgba(22, 163, 74, 0.25)'
                                                : '0 6px 16px rgba(234, 88, 12, 0.25)',
                                        }}
                                    >
                                        <i
                                            className={
                                                isFilterEnabled
                                                    ? 'icofont-shield-alt'
                                                    : 'icofont-warning'
                                            }
                                        ></i>
                                    </div>
                                    <div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <h5 className="fw-bold mb-0 text-dark">
                                                Abusive Filter Words
                                            </h5>
                                            <span
                                                className={`badge rounded-pill px-3 py-1 fw-semibold ${isFilterEnabled
                                                    ? 'bg-success text-white'
                                                    : 'bg-secondary text-white'
                                                    }`}
                                                style={{ fontSize: '12px' }}
                                            >
                                                {statusLoading ? (
                                                    'Checking...'
                                                ) : isFilterEnabled ? (
                                                    <>
                                                        <i className="icofont-check-circled me-1"></i> Active
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="icofont-close-circled me-1"></i> Disabled
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                        <p
                                            className="text-muted mb-0 small mt-1"
                                            style={{ lineHeight: '1.4' }}
                                        >
                                            {isFilterEnabled
                                                ? 'Filter is actively screening consultation chats and messages. Inappropriate or abusive words are blocked automatically based on the uploaded words.'
                                                : 'Filter is currently inactive. Chats and messages will not be screened against abusive words until enabled.'}
                                        </p>
                                    </div>
                                </div>

                                {/* Right: Refresh Button + Toggle Switch (Cleanly Aligned) */}
                                <div className="d-flex align-items-center gap-3 ms-auto" style={{ flexShrink: 0 }}>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                                        onClick={fetchFilterStatus}
                                        disabled={statusLoading || toggleUpdating}
                                        title="Refresh Status"
                                        style={{ fontSize: '13px' }}
                                    >
                                        <i
                                            className={`icofont-refresh me-1 ${statusLoading ? 'fa-spin' : ''
                                                }`}
                                        ></i>
                                        Refresh
                                    </button>

                                    <div className="form-check form-switch m-0 d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="abusiveFilterToggle"
                                            checked={isFilterEnabled}
                                            onChange={handleToggleFilter}
                                            disabled={statusLoading || toggleUpdating}
                                            style={{
                                                cursor:
                                                    statusLoading || toggleUpdating
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                width: '3em',
                                                height: '1.6em',
                                                backgroundColor: isFilterEnabled
                                                    ? '#16a34a'
                                                    : '#cbd5e1',
                                                borderColor: isFilterEnabled
                                                    ? '#16a34a'
                                                    : '#94a3b8',
                                            }}
                                        />
                                        <label
                                            className="form-check-label fw-bold ms-2"
                                            htmlFor="abusiveFilterToggle"
                                            style={{
                                                cursor:
                                                    statusLoading || toggleUpdating
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                userSelect: 'none',
                                                fontSize: '14px',
                                                color: isFilterEnabled ? '#15803d' : '#64748b',
                                            }}
                                        >
                                            {toggleUpdating ? (
                                                <span className="text-muted">Updating...</span>
                                            ) : isFilterEnabled ? (
                                                'Enabled'
                                            ) : (
                                                'Disabled'
                                            )}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================= */}
                {/* 2. FILE UPLOAD CARD (Full Width - Col 12)          */}
                {/* ================================================= */}
                <div className="col-12">
                    <div
                        className="card border-0 shadow-sm"
                        style={{ borderRadius: '14px' }}
                    >
                        <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div>
                                <h6 className="fw-bold mb-1 text-dark">
                                    {/* <i className="icofont-upload-alt text-primary me-2"></i> */}
                                    Upload Abusive Words File
                                </h6>
                                <p className="text-muted small mb-0">
                                    Upload an Excel (.xlsx / .xls) file containing words to be filtered.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 px-3 py-2 shadow-sm"
                                style={{ borderRadius: '8px', fontWeight: 600 }}
                                onClick={handleDownloadSampleFile}
                                title="Download sample template Excel file"
                            >
                                <i className="icofont-download"></i>
                                <span>Download Sample File</span>
                            </button>
                        </div>

                        <div className="card-body p-4">
                            {/* Hidden File Input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                            />

                            {/* Drag and Drop Zone */}
                            {!selectedFile ? (
                                <div
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-center p-4"
                                    style={{
                                        border: dragActive
                                            ? '2px dashed #3b82f6'
                                            : '2px dashed #cbd5e1',
                                        borderRadius: '12px',
                                        backgroundColor: dragActive
                                            ? '#eff6ff'
                                            : '#f8fafc',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <div
                                        className="mx-auto mb-3"
                                        style={{
                                            width: '64px',
                                            height: '64px',
                                            borderRadius: '50%',
                                            backgroundColor: '#e0f2fe',
                                            color: '#0284c7',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '28px',
                                        }}
                                    >
                                        <i className="icofont-file-excel"></i>
                                    </div>
                                    <h6 className="fw-bold text-dark mb-1">
                                        Drag & drop your Excel file here, or{' '}
                                        <span className="text-primary text-decoration-underline">
                                            Browse
                                        </span>
                                    </h6>
                                    <p className="text-muted small mb-2">
                                        Supported format: Microsoft Excel (.xlsx, .xls)
                                    </p>
                                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                        <span className="badge bg-light text-secondary border px-3 py-1">
                                            Max allowed size: {MAX_FILE_SIZE_MB} MB
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                /* Selected File Preview Box */
                                <div
                                    className="p-3 mb-3 border rounded-3"
                                    style={{
                                        backgroundColor: '#f8fafc',
                                        borderColor: '#e2e8f0',
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                style={{
                                                    width: '46px',
                                                    height: '46px',
                                                    borderRadius: '10px',
                                                    backgroundColor: '#10b981',
                                                    color: '#ffffff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '22px',
                                                }}
                                            >
                                                <i className="icofont-file-excel"></i>
                                            </div>
                                            <div className="overflow-hidden" style={{ minWidth: 0, maxWidth: '100%' }}>
                                                <h6 className="fw-bold mb-0 text-dark text-break">
                                                    {fileStats?.fileName || selectedFile.name}
                                                </h6>
                                                <span className="text-muted small d-inline-flex flex-wrap align-items-center gap-1 mt-1">
                                                    <span>Size: {fileStats?.fileSize}</span>
                                                    <span>•</span>
                                                    <span>Sheet: {fileStats?.activeSheet || 'Sheet 1'}</span>
                                                    {fileStats?.keyName && (
                                                        <>
                                                            <span>•</span>
                                                            <span>
                                                                Key: <span className="badge bg-secondary-subtle text-secondary border px-2 py-0.5 rounded">{fileStats.keyName}</span>
                                                            </span>
                                                        </>
                                                    )}
                                                    <span>•</span>
                                                    <span>
                                                        Words detected:{' '}
                                                        <strong className="text-primary">
                                                            {typeof fileStats?.totalWords === 'number'
                                                                ? fileStats.totalWords.toLocaleString()
                                                                : fileStats?.totalWords ?? 'Processing...'}
                                                        </strong>
                                                    </span>
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                            onClick={handleClearFile}
                                            disabled={isUploading}
                                        >
                                            <i className="icofont-ui-delete me-1"></i> Remove
                                        </button>
                                    </div>

                                    {/* Sample Words Chips Preview */}
                                    {previewWords.length > 0 && (
                                        <div
                                            className="p-3 rounded-2"
                                            style={{
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e2e8f0',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                                <span
                                                    className="fw-bold text-uppercase"
                                                    style={{ fontSize: '11px', color: '#64748b' }}
                                                >
                                                    Sample Words Preview (First {previewWords.length} of{' '}
                                                    {typeof fileStats?.totalWords === 'number'
                                                        ? fileStats.totalWords.toLocaleString()
                                                        : previewWords.length}
                                                    )
                                                </span>
                                                <span
                                                    className="badge bg-primary-subtle text-primary"
                                                    style={{ fontSize: '11px' }}
                                                >
                                                    Ready to upload
                                                </span>
                                            </div>
                                            <div
                                                className="d-flex flex-wrap gap-1"
                                                style={{
                                                    maxHeight: '160px',
                                                    overflowY: 'auto',
                                                    overflowX: 'hidden',
                                                    wordBreak: 'break-word',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                {previewWords.map((word, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="badge bg-light text-dark border px-2 py-1"
                                                        style={{
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            maxWidth: '100%',
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        {word}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Upload Progress Bar */}
                                    {isUploading && (
                                        <div className="mt-3">
                                            <div className="d-flex justify-content-between small text-muted mb-1">
                                                <span>Uploading file...</span>
                                                <span>{uploadProgress}%</span>
                                            </div>
                                            <div
                                                className="progress"
                                                style={{ height: '8px', borderRadius: '4px' }}
                                            >
                                                <div
                                                    className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                                    role="progressbar"
                                                    style={{ width: `${uploadProgress}%` }}
                                                    aria-valuenow={uploadProgress}
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                ></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Submit Upload Button */}
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                {selectedFile && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4 rounded-pill"
                                        onClick={handleClearFile}
                                        disabled={isUploading}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-primary px-4 rounded-pill"
                                    onClick={handleUploadSubmit}
                                    disabled={!selectedFile || isUploading}
                                    style={{
                                        minWidth: '180px',
                                        backgroundColor: 'var(--primary-color, #1976d2)',
                                    }}
                                >
                                    {isUploading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="icofont-cloud-upload me-2"></i>
                                            Upload & Apply
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================= */}
                {/* 3. ACTIVE FILTER WORDS LIST (AstrologerList UI)   */}
                {/* ================================================= */}
                <div className="col-12 mt-4">
                    <div
                        className="card mb-3"
                        style={{
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: 'none',
                            padding: '16px 20px',
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ gap: 2 }}>
                            <Box>
                                <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2" style={{ fontSize: '16px' }}>
                                    Active Filter Words
                                    {/* <span
                                        className="badge rounded-pill"
                                        style={{
                                            backgroundColor: '#eff6ff',
                                            color: '#2563eb',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            padding: '4px 10px',
                                        }}
                                    >
                                        {paginationDetail?.totalDocs?.toLocaleString() || 0}
                                    </span> */}
                                </h6>
                                <p className="text-muted small mb-0">
                                    All words currently stored and validated by the chat filter engine.
                                </p>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                {/* View Mode Toggle */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

                                    <Button
                                        variant={viewMode === 'chips' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setViewMode('chips')}
                                        sx={{
                                            fontSize: '13px',
                                            minHeight: 30,
                                            padding: '4px 16px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                            backgroundColor: viewMode === 'chips' ? 'var(--primary-color, #E7B242)' : 'transparent',
                                            color: viewMode === 'chips' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                                            borderColor: viewMode === 'chips' ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.23)',
                                            '&:hover': {
                                                backgroundColor: viewMode === 'chips' ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.04)',
                                                color: viewMode === 'chips' ? '#ffffff' : 'var(--primary-color, #E7B242)',
                                                borderColor: 'var(--primary-color, #E7B242)',
                                            },
                                        }}
                                    >
                                        <i className="icofont-tags me-1"></i> Chips
                                    </Button>
                                    <Button
                                        variant={viewMode === 'table' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setViewMode('table')}
                                        sx={{
                                            fontSize: '13px',
                                            minHeight: 30,
                                            padding: '4px 16px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                            backgroundColor: viewMode === 'table' ? 'var(--primary-color, #E7B242)' : 'transparent',
                                            color: viewMode === 'table' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                                            borderColor: viewMode === 'table' ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.23)',
                                            '&:hover': {
                                                backgroundColor: viewMode === 'table' ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.04)',
                                                color: viewMode === 'table' ? '#ffffff' : 'var(--primary-color, #E7B242)',
                                                borderColor: 'var(--primary-color, #E7B242)',
                                            },
                                        }}
                                    >
                                        <i className="icofont-table me-1"></i> Table
                                    </Button>
                                </Box>

                                {/* Show / Hide All Button */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={toggleAllWords}
                                    sx={{
                                        fontSize: '13px',
                                        px: 2,
                                        py: 0.6,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        color: '#64748b',
                                        borderColor: '#cbd5e1',
                                        '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' },
                                    }}
                                >
                                    <i className={`icofont-${showAllWords ? 'eye-blocked' : 'eye'} me-1`}></i>
                                    {showAllWords ? 'Hide All' : 'Show All'}
                                </Button>

                                {/* Refresh Button */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => fetchFilteredWords(paginationModel.page + 1, paginationModel.pageSize)}
                                    disabled={wordsLoading}
                                    sx={{
                                        fontSize: '13px',
                                        px: 2,
                                        py: 0.6,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        color: '#64748b',
                                        borderColor: '#cbd5e1',
                                        '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' },
                                    }}
                                >
                                    <i className={`icofont-refresh me-1 ${wordsLoading ? 'fa-spin' : ''}`}></i>
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    </div>

                    {/* Table Card (MUI DataGrid + TablePagination) */}
                    <div
                        className="card"
                        style={{
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        <div className="card-body" style={{ padding: 0 }}>
                            {viewMode === 'table' ? (
                                <Box sx={{ width: '100%' }}>
                                    <DataGrid
                                        loading={wordsLoading}
                                        columns={columns}
                                        rows={rows}
                                        disableRowSelectionOnClick
                                        paginationMode="server"
                                        hideFooterPagination
                                        getRowId={(row) => row.id}
                                        autoHeight
                                        sx={{
                                            border: 'none',
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f8fafc',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                borderBottom: '1px solid #e2e8f0',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid #f1f5f9',
                                                fontSize: '13px',
                                            },
                                            '& .MuiDataGrid-cell:focus': {
                                                outline: 'none',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: '#f8fafc',
                                            },
                                        }}
                                    />
                                </Box>
                            ) : (
                                /* CHIPS VIEW */
                                <div className="p-3" style={{ minHeight: '220px', backgroundColor: '#f8fafc' }}>
                                    {wordsLoading ? (
                                        <div className="text-center py-5">
                                            <CircularProgress size={32} />
                                            <p className="text-muted small mt-2 mb-0">Loading abusive words from server...</p>
                                        </div>
                                    ) : wordsList.length === 0 ? (
                                        <div className="text-center py-5">
                                            <h6 className="fw-bold text-dark mb-1">No filter words available</h6>
                                            <p className="text-muted small mb-0">Upload an Excel file above to populate the active abusive filter words.</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(195px, 1fr))', gap: '10px' }}>
                                            {wordsList.map((item, idx) => {
                                                const sNo = paginationModel.page * paginationModel.pageSize + idx + 1;
                                                const isCopied = copiedWord === item.word;
                                                const isHidden = isWordHidden(item.id);
                                                return (
                                                    <div
                                                        key={item._id || item.id || idx}
                                                        className="bg-white border rounded-3 px-3 py-2 shadow-sm d-flex align-items-center justify-content-between gap-2"
                                                        style={{
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            minHeight: '42px',
                                                            borderColor: '#e2e8f0',
                                                            transition: 'all 0.15s ease',
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                                                            <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '600', flexShrink: 0 }}>
                                                                #{sNo}
                                                            </span>
                                                            {isHidden ? (
                                                                <span className="text-muted fw-bold" style={{ letterSpacing: '2px', fontSize: '13px' }}>
                                                                    ••••••••
                                                                </span>
                                                            ) : (
                                                                <span className="fw-semibold text-danger text-truncate" title={item.word} style={{ fontSize: '13px' }}>
                                                                    {item.word}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                            <i
                                                                className={`fs-6 icofont-${isHidden ? 'eye' : 'eye-blocked'} text-muted`}
                                                                style={{ fontSize: '13px', cursor: 'pointer' }}
                                                                onClick={() => toggleWordVisibility(item.id)}
                                                                title={isHidden ? 'Show Word' : 'Hide Word'}
                                                            ></i>
                                                            <i
                                                                className={`fs-6 ${isCopied ? 'icofont-check text-success' : 'icofont-copy text-muted'}`}
                                                                style={{ fontSize: '12px', cursor: 'pointer' }}
                                                                onClick={() => handleCopyWord(item.word)}
                                                                title={isCopied ? 'Copied!' : 'Copy Word'}
                                                            ></i>
                                                            <i
                                                                className="fs-6 icofont-trash text-danger"
                                                                style={{ fontSize: '13px', cursor: 'pointer' }}
                                                                onClick={() => handleDeleteClick(item)}
                                                                title="Delete Word"
                                                            ></i>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            <TablePagination
                                component="div"
                                count={paginationDetail?.totalDocs || 0}
                                page={paginationModel.page}
                                onPageChange={handleChangePage}
                                rowsPerPage={paginationModel.pageSize}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                labelRowsPerPage="Rows per page:"
                            />
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Dialog (Matching AstrologerList) */}
                <Dialog open={openDeleteDialog} onClose={handleCloseDelete} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="icofont-warning" style={{ fontSize: '20px' }}></i> Delete Abusive Word
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: '#334155', mb: 2 }}>
                            Are you sure you want to delete this abusive word from the chat filter engine?
                        </DialogContentText>
                        {wordToDelete && (
                            <Box sx={{ p: 2, my: 1.5, bgcolor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', textAlign: 'center', color: '#dc2626', fontWeight: 700, fontSize: '1.1rem' }}>
                                {wordToDelete.word}
                            </Box>
                        )}
                        <DialogContentText sx={{ fontSize: '12px', color: '#64748b' }}>
                            Once deleted, this word will no longer be blocked/masked in customer and astrologer chats.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={handleCloseDelete} disabled={isDeleting} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                            Cancel
                        </Button>
                        <Button onClick={confirmDeleteWord} color="error" variant="contained" disabled={isDeleting} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
}

export default ValidateAbusiveWords;
